import { useEffect, useState } from 'react';

/**
 * usePageVisibility — hook around the Page Visibility API.
 *
 * Returns `true` while the document is visible to the user (active tab,
 * window not minimised). Switches to `false` when the user navigates to
 * another tab, screen-shares with a different app focused, etc.
 *
 * The lesson player uses this to:
 *   1. Pause embedded video — anti-piracy: a hidden tab continuing to play
 *      the video is the classic "screen-record while pretending to read" trick.
 *   2. Pause the heartbeat timer that records "time on content" — if the
 *      student isn't looking, they aren't "on" the content.
 *
 * Implementation is just the standard `visibilitychange` event with a tiny
 * synchronous initial read so React's first render is correct.
 */
export function usePageVisibility(): boolean {
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState !== 'hidden';
  });

  useEffect(() => {
    const handle = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', handle);
    return () => document.removeEventListener('visibilitychange', handle);
  }, []);

  return visible;
}
